import { create } from "zustand";

export interface FileStatus {
  id: number | string;
  name: string;
  isUploading: boolean;
  embeddingProgress: number;
  uploadProgress?: number; // 0-100 for chunked upload progress
  num_sdr?: number;
  num_sdr_not_embedded?: number;
  // Backend-provided fields (from processing-status API)
  status?: string; // Raw status: uploaded, converting_to_pdf, chunking, completed, etc.
  display_status?: string; // User-friendly status text from backend
  is_processing?: boolean; // Whether currently processing (from backend)
  progress_percentage?: number; // Progress 0-100 (from backend)
  // Legacy fields for backward compatibility
  processingStage?: string;
  processingProgress?: number;
}

interface FileStatusStore {
  fileStatuses: Map<string | number, FileStatus>;
  updateFileStatus: (
    fileId: string | number,
    status: Partial<FileStatus>,
  ) => void;
  getFileStatus: (fileId: string | number) => FileStatus | undefined;
  getFileStatusByName: (fileName: string) => FileStatus | undefined;
  removeFileStatus: (fileId: string | number) => void;
  clearAllFileStatuses: () => void;
}

export const useFileStatusStore = create<FileStatusStore>((set, get) => ({
  fileStatuses: new Map(),

  updateFileStatus: (fileId: string | number, status: Partial<FileStatus>) => {
    set((state) => {
      const newMap = new Map(state.fileStatuses);
      const existing = newMap.get(fileId);

      if (existing) {
        newMap.set(fileId, { ...existing, ...status });
      } else {
        // Create new status if it doesn't exist
        newMap.set(fileId, {
          id: fileId,
          name: status.name || "",
          isUploading: status.isUploading || false,
          embeddingProgress: status.embeddingProgress || 0,
          num_sdr: status.num_sdr,
          num_sdr_not_embedded: status.num_sdr_not_embedded,
          processingStage: status.processingStage,
          processingProgress: status.processingProgress,
        });
      }

      return { fileStatuses: newMap };
    });
  },

  getFileStatus: (fileId: string | number) => {
    return get().fileStatuses.get(fileId);
  },

  getFileStatusByName: (fileName: string) => {
    const fileStatuses = get().fileStatuses;
    for (const [, status] of fileStatuses) {
      if (status.name === fileName) {
        return status;
      }
    }
    return undefined;
  },

  removeFileStatus: (fileId: string | number) => {
    set((state) => {
      const newMap = new Map(state.fileStatuses);
      newMap.delete(fileId);
      return { fileStatuses: newMap };
    });
  },

  clearAllFileStatuses: () => {
    set({ fileStatuses: new Map() });
  },
}));
